import type { Dayjs } from 'dayjs'
import type { Workbook } from 'exceljs'
import type { QueryKey, QueryFunctionContext } from '@tanstack/react-query'
import type { ColumnMeta } from '@tanstack/react-table'
import type { Address } from '@/lib/services/addresses/addresses.types'
import type {
  IndexTableExportType,
  IndexTableExportValue,
  IndexTableExportColumnConfig,
} from './IndexTable'
import { readProperty } from '@/lib/utils'

import { downloadBlob } from '@/lib/download'
import dayjs from '@/lib/dayjs'
import queryClient from '@/lib/queryClient'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useMutation } from '@tanstack/react-query'

export interface IndexTableExportColumnSource<TData> {
  id?: string
  accessorKey?: string
  accessorFn?: (row: TData, rowIndex: number) => unknown
  header?: unknown
  meta?: Pick<
    ColumnMeta<TData, unknown>,
    'kind' | 'label' | 'exportable' | 'exportLabel' | 'exportType' | 'exportValue' | 'exportColumns'
  >
  columnDef?: IndexTableExportColumnSource<TData>
}
export interface IndexTableExportColumn<TData> {
  id: string
  label: string
  type: IndexTableExportType
  value: (
    row: TData,
    context: Parameters<IndexTableExportValue<TData>>[1] & {
      column?: IndexTableExportColumn<TData>
    }
  ) => unknown
}
export interface IndexTableExportSheet<TData> {
  columns?: IndexTableExportColumnSource<TData>[]
  rows?: TData[]
  sheetName?: string
}
type IndexTableExportSheets<TSheets extends readonly unknown[]> = {
  [TIndex in keyof TSheets]: IndexTableExportSheet<TSheets[TIndex]>
}
interface SpreadsheetDownloadOptions<
  TData,
  TSheets extends readonly unknown[],
> extends IndexTableExportSheet<TData> {
  sheets?: IndexTableExportSheets<TSheets>
  filename?: unknown
  generatedAt?: Date
  signal?: AbortSignal
}
interface NormalizedExportSheet {
  exportColumns: { label: string; type: IndexTableExportType }[]
  buildRows: () => unknown[][]
  sheetName: string
}
type ExportAddress = Pick<
  Address,
  'name' | 'line1' | 'line2' | 'city' | 'state' | 'postal_code' | 'country' | 'is_verified'
>
type IndexResponse<TData> =
  | TData[]
  | { data?: TData[]; meta?: { last_page?: number | string | null } }
  | null
  | undefined
interface FetchPageArguments {
  page: number
  pageSize: number
  signal?: AbortSignal
}
interface ExportQueryOptions<TResult, TKey extends QueryKey> {
  queryKey: TKey
  meta?: QueryFunctionContext<TKey>['meta']
  queryFn?: (
    context: Pick<QueryFunctionContext<TKey>, 'queryKey' | 'meta'> &
      Partial<Pick<QueryFunctionContext<TKey>, 'signal'>>
  ) => TResult | Promise<TResult>
}

export const INDEX_TABLE_EXPORT_PAGE_SIZE = 100

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const XLSX_EXTENSION_PATTERN = /\.xlsx$/i
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

function createAbortError(): Error {
  const error = new Error('Export cancelled')
  error.name = 'AbortError'

  return error
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw createAbortError()
  }
}

function isAbortError(error: unknown): boolean {
  return (
    readProperty(error, 'name') === 'AbortError' ||
    readProperty(error, 'name') === 'CanceledError' ||
    readProperty(error, 'code') === 'ERR_CANCELED'
  )
}

function waitForBrowserPaint(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }

  return new Promise<void>((resolve) => {
    const resolveAfterPaintOpportunity = () => window.setTimeout(resolve, 0)

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(resolveAfterPaintOpportunity)
      return
    }

    resolveAfterPaintOpportunity()
  })
}

function getColumnDef<TData>(
  column: IndexTableExportColumnSource<TData> | null | undefined
): IndexTableExportColumnSource<TData> {
  return column?.columnDef ?? column ?? {}
}

function getColumnMeta<TData>(column: IndexTableExportColumnSource<TData>) {
  return getColumnDef(column).meta ?? {}
}

function getColumnId<TData>(column: IndexTableExportColumnSource<TData>) {
  const columnDef = getColumnDef(column)

  return columnDef.id ?? columnDef.accessorKey ?? column?.id
}

function getHeaderText<TData>(column: IndexTableExportColumnSource<TData>) {
  const columnDef = getColumnDef(column)
  const meta = getColumnMeta(column)
  const columnId = getColumnId(column)
  const headerValue = columnDef.header

  if (columnId === 'actions' || meta.kind === 'actions') {
    return ''
  }

  if (headerValue === '') {
    return ''
  }

  if (meta.label) {
    return meta.label
  }

  if (typeof headerValue === 'string') {
    return headerValue
  }

  return columnId
}

function getPathValue(source: unknown, path: string | undefined): unknown {
  if (!path || typeof path !== 'string') {
    return undefined
  }

  return path
    .split('.')
    .reduce<unknown>(
      (value, key) => (value == null ? undefined : Reflect.get(new Object(value), key)),
      source
    )
}

function getDefaultExportValue<TData>(
  column: IndexTableExportColumnSource<TData>,
  row: TData,
  rowIndex: number
): unknown {
  const columnDef = getColumnDef(column)
  const columnId = getColumnId(column)

  if (typeof columnDef.accessorFn === 'function') {
    return columnDef.accessorFn(row, rowIndex)
  }

  return (
    getPathValue(row, columnDef.accessorKey) ??
    (row == null ? undefined : Reflect.get(new Object(row), String(columnId)))
  )
}

function normalizeExportColumn<TData>(
  column: IndexTableExportColumnSource<TData>,
  exportColumn: IndexTableExportColumnConfig<TData> & { label: string },
  index: number
): IndexTableExportColumn<TData>
function normalizeExportColumn<TData>(
  column: IndexTableExportColumnSource<TData>,
  exportColumn: IndexTableExportColumnConfig<TData>,
  index: number
): Omit<IndexTableExportColumn<TData>, 'label'> & { label: string | undefined }
function normalizeExportColumn<TData>(
  column: IndexTableExportColumnSource<TData>,
  exportColumn: IndexTableExportColumnConfig<TData>,
  index: number
) {
  const meta = getColumnMeta(column)
  const columnId = getColumnId(column)
  const label = exportColumn.exportLabel ?? exportColumn.label ?? getHeaderText(column)
  const value = exportColumn.exportValue ?? exportColumn.value ?? meta.exportValue

  return {
    id: exportColumn.id ?? `${columnId}_${index}`,
    label,
    type: exportColumn.exportType ?? exportColumn.type ?? meta.exportType ?? 'text',
    value:
      typeof value === 'function'
        ? value
        : (row: TData, { rowIndex }: { rowIndex: number }) =>
            getDefaultExportValue(column, row, rowIndex),
  }
}

export function createIndexTableExportColumn<TData>({
  id,
  label,
  type = 'text',
  value,
}: {
  id: string
  label: string
  type?: IndexTableExportType
  value: (row: TData, rowIndex: number) => unknown
}): IndexTableExportColumnSource<TData> {
  return {
    id,
    header: label,
    accessorFn: value,
    meta: {
      label,
      exportType: type,
    },
  }
}

export function createAddressExportColumns<TData>({
  idPrefix,
  labelPrefix,
  getAddress,
  getName = (row) => getAddress(row)?.name,
  includeVerified = false,
}: {
  idPrefix: string
  labelPrefix: string
  getAddress: (row: TData) => ExportAddress | null | undefined
  getName?: (row: TData) => unknown
  includeVerified?: boolean
}): IndexTableExportColumnSource<TData>[] {
  const columns = [
    createIndexTableExportColumn<TData>({
      id: `${idPrefix}_name`,
      label: `${labelPrefix} Name`,
      value: (row) => getName(row) || '',
    }),
    createIndexTableExportColumn<TData>({
      id: `${idPrefix}_line1`,
      label: `${labelPrefix} Line 1`,
      value: (row) => getAddress(row)?.line1 || '',
    }),
    createIndexTableExportColumn<TData>({
      id: `${idPrefix}_line2`,
      label: `${labelPrefix} Line 2`,
      value: (row) => getAddress(row)?.line2 || '',
    }),
    createIndexTableExportColumn<TData>({
      id: `${idPrefix}_city`,
      label: `${labelPrefix} City`,
      value: (row) => getAddress(row)?.city || '',
    }),
    createIndexTableExportColumn<TData>({
      id: `${idPrefix}_state`,
      label: `${labelPrefix} State`,
      value: (row) => getAddress(row)?.state || '',
    }),
    createIndexTableExportColumn<TData>({
      id: `${idPrefix}_postal_code`,
      label: `${labelPrefix} Postal Code`,
      value: (row) => getAddress(row)?.postal_code || '',
    }),
    createIndexTableExportColumn<TData>({
      id: `${idPrefix}_country`,
      label: `${labelPrefix} Country`,
      value: (row) => getAddress(row)?.country || '',
    }),
  ]

  if (!includeVerified) {
    return columns
  }

  return [
    ...columns,
    createIndexTableExportColumn<TData>({
      id: `${idPrefix}_verified`,
      label: `${labelPrefix} Verified`,
      type: 'boolean',
      value: (row) => {
        const address = getAddress(row)

        return address ? Boolean(address.is_verified) : null
      },
    }),
  ]
}

export function getIndexDataTableExportColumns<TData>(
  columns: IndexTableExportColumnSource<TData>[] = []
): IndexTableExportColumn<TData>[] {
  return columns.flatMap((column) => {
    const meta = getColumnMeta(column)
    const columnId = getColumnId(column)
    const label = meta.exportLabel ?? getHeaderText(column)

    if (meta.exportable === false || columnId === 'actions' || meta.kind === 'actions' || !label) {
      return []
    }

    if (Array.isArray(meta.exportColumns)) {
      return meta.exportColumns
        .map((exportColumn, index) => normalizeExportColumn(column, exportColumn, index))
        .filter((exportColumn): exportColumn is IndexTableExportColumn<TData> =>
          Boolean(exportColumn.label)
        )
    }

    return [
      normalizeExportColumn(
        column,
        {
          id: columnId,
          label,
          exportLabel: meta.exportLabel,
          exportType: meta.exportType,
          exportValue: meta.exportValue,
        },
        0
      ),
    ]
  })
}

function createLocalDateFromDayjs(
  date: Dayjs,
  { includeTime = false }: { includeTime?: boolean } = {}
): Date {
  // Excel dates do not carry timezone data, so write the org-time wall clock into a local Date.
  return new Date(
    date.year(),
    date.month(),
    date.date(),
    includeTime ? date.hour() : 12,
    includeTime ? date.minute() : 0,
    includeTime ? date.second() : 0,
    includeTime ? date.millisecond() : 0
  )
}

function normalizeDateValue(
  value: unknown,
  { includeTime = false }: { includeTime?: boolean } = {}
): string | Date {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  let date: Dayjs

  try {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      value instanceof Date ||
      dayjs.isDayjs(value)
    ) {
      date = dayjs(value)
    } else {
      // Day.js delegates these inputs to Date; preserve its native coercion at this boundary.
      const nativeDate: unknown = Reflect.construct(Date, [value])
      // oxlint-disable-next-line typescript/no-base-to-string -- retain the existing export date fallback for unsupported values
      if (!(nativeDate instanceof Date)) return String(value ?? '')
      date = dayjs(nativeDate)
    }
  } catch {
    // oxlint-disable-next-line typescript/no-base-to-string -- preserve raw export text when legacy date coercion fails
    return String(value ?? '')
  }

  if (!date.isValid()) {
    // oxlint-disable-next-line typescript/no-base-to-string -- preserve raw export text for invalid dates
    return String(value ?? '')
  }

  if (!includeTime && typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
    return createLocalDateFromDayjs(dayjs(value))
  }

  return createLocalDateFromDayjs(date, { includeTime })
}

function normalizeExportValue(value: unknown, type: IndexTableExportType): unknown {
  if (value === null || value === undefined) {
    return ''
  }

  if (type === 'number' || type === 'currency' || type === 'integer') {
    const numericValue = Number(value)

    return Number.isFinite(numericValue) ? numericValue : ''
  }

  if (type === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (type === 'date') {
    return normalizeDateValue(value)
  }

  if (type === 'datetime') {
    return normalizeDateValue(value, { includeTime: true })
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ')
  }

  if (value instanceof Date) {
    return value
  }

  if (typeof value === 'object') {
    return ''
  }

  return value
}

function getExportColumnFormat(type: IndexTableExportType): string | undefined {
  if (type === 'currency') {
    return '$#,##0.00'
  }

  if (type === 'number') {
    return '0.00'
  }

  if (type === 'integer') {
    return '0'
  }

  if (type === 'date') {
    return 'mmm d, yyyy'
  }

  if (type === 'datetime') {
    return 'mmm d, yyyy h:mm AM/PM'
  }

  return undefined
}

function getCellWidthText(value: unknown): string {
  if (value instanceof Date) {
    return value.toLocaleDateString()
  }

  // oxlint-disable-next-line typescript/no-base-to-string -- measure the existing native text representation of arbitrary export cells
  return String(value ?? '')
}

function getColumnWidth(label: string, rows: unknown[][], columnIndex: number): number {
  const maxLength = rows.reduce((length, row) => {
    return Math.max(length, getCellWidthText(row[columnIndex]).length)
  }, label.length)

  return Math.min(Math.max(maxLength + 2, 10), 42)
}

function getWorksheetTitle(title: unknown): string {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve worksheet title coercion from existing callers
  return String(title || 'Export')
    .replace(/[\][*?/\\:]/g, ' ')
    .slice(0, 31)
    .trim()
}

function padTimestampPart(value: number): string {
  return String(value).padStart(2, '0')
}

function formatExportTimestamp(generatedAt: Date): string {
  return [
    generatedAt.getFullYear(),
    padTimestampPart(generatedAt.getMonth() + 1),
    padTimestampPart(generatedAt.getDate()),
    padTimestampPart(generatedAt.getHours()),
    padTimestampPart(generatedAt.getMinutes()),
    padTimestampPart(generatedAt.getSeconds()),
  ].join('-')
}

export function getIndexTableExportFilename(filename: unknown, generatedAt = new Date()): string {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve export filename coercion from existing callers
  const normalizedFilename = String(filename || 'export')
  const baseFilename = normalizedFilename.replace(XLSX_EXTENSION_PATTERN, '')

  return `${baseFilename}-${formatExportTimestamp(generatedAt)}.xlsx`
}

function getExcelColumnName(columnNumber: number): string {
  let name = ''
  let value = columnNumber

  while (value > 0) {
    const remainder = (value - 1) % 26
    name = String.fromCharCode(65 + remainder) + name
    value = Math.floor((value - 1) / 26)
  }

  return name
}

function normalizeExportSheet<TData>(
  sheet: IndexTableExportSheet<TData>,
  sheetName: string | undefined,
  index: number
): NormalizedExportSheet {
  const exportColumns = getIndexDataTableExportColumns(sheet.columns)
  if (!exportColumns.length) throw new Error('No exportable columns were found.')
  const rows = sheet.rows ?? []
  return {
    exportColumns,
    sheetName: sheet.sheetName ?? sheetName ?? `Export ${index + 1}`,
    buildRows: () =>
      rows.map((row, rowIndex) =>
        exportColumns.map((exportColumn) =>
          normalizeExportValue(
            exportColumn.value(row, { rowIndex, column: exportColumn }),
            exportColumn.type
          )
        )
      ),
  }
}
function isExportSheets<TSheets extends readonly unknown[]>(
  sheets: IndexTableExportSheets<TSheets> | undefined
): sheets is IndexTableExportSheets<TSheets> {
  return Array.isArray(sheets)
}
function getIndexTableExportSheets<TData, TSheets extends readonly unknown[]>({
  columns,
  rows = [],
  sheetName = 'Export',
  sheets,
}: SpreadsheetDownloadOptions<TData, TSheets>): NormalizedExportSheet[] {
  return isExportSheets(sheets)
    ? sheets.map((sheet, index) => normalizeExportSheet(sheet, sheetName, index))
    : [normalizeExportSheet({ columns, rows, sheetName }, sheetName, 0)]
}

function addExportSheet(
  workbook: Workbook,
  { exportColumns, buildRows, sheetName }: NormalizedExportSheet
): void {
  const worksheet = workbook.addWorksheet(getWorksheetTitle(sheetName) || 'Export')
  const normalizedRows = buildRows()

  worksheet.views = [{ state: 'frozen', ySplit: 1 }]
  worksheet.addRow(exportColumns.map((column) => column.label))
  worksheet.addRows(normalizedRows)

  const headerRow = worksheet.getRow(1)
  headerRow.font = { bold: true }

  exportColumns.forEach((column, index) => {
    const excelColumn = worksheet.getColumn(index + 1)
    const format = getExportColumnFormat(column.type)

    excelColumn.width = getColumnWidth(column.label, normalizedRows, index)

    if (format) {
      excelColumn.numFmt = format
    }
  })

  if (exportColumns.length) {
    worksheet.autoFilter = `A1:${getExcelColumnName(exportColumns.length)}1`
  }
}

export async function createIndexDataTableSpreadsheetDownload<
  TData,
  TSheets extends readonly unknown[],
>({
  columns,
  rows,
  sheets,
  filename,
  sheetName = 'Export',
  generatedAt = new Date(),
  signal,
}: SpreadsheetDownloadOptions<TData, TSheets>): Promise<{ blob: Blob; filename: string }> {
  throwIfAborted(signal)

  const exportSheets = getIndexTableExportSheets({ columns, rows, sheetName, sheets })

  const ExcelJSModule = await import('exceljs')
  throwIfAborted(signal)

  const ExcelJS = ExcelJSModule.default ?? ExcelJSModule
  const workbook = new ExcelJS.Workbook()

  workbook.creator = 'OneStem'
  workbook.created = generatedAt
  exportSheets.forEach((sheet) => addExportSheet(workbook, sheet))

  const buffer = await workbook.xlsx.writeBuffer()
  throwIfAborted(signal)

  const blob = new Blob([buffer], { type: XLSX_CONTENT_TYPE })

  return {
    blob,
    filename: getIndexTableExportFilename(filename, generatedAt),
  }
}

export async function exportIndexDataTableSpreadsheet<TData, TSheets extends readonly unknown[]>(
  options: SpreadsheetDownloadOptions<TData, TSheets>
): Promise<void> {
  const { blob, filename } = await createIndexDataTableSpreadsheetDownload(options)

  downloadBlob(blob, filename)
}

export function getRowsFromIndexResponse<TData>(response: IndexResponse<TData>): TData[] {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

export async function fetchAllIndexTablePages<TData>({
  fetchPage,
  pageSize = INDEX_TABLE_EXPORT_PAGE_SIZE,
  signal,
}: {
  fetchPage: (args: FetchPageArguments) => IndexResponse<TData> | Promise<IndexResponse<TData>>
  pageSize?: number
  signal?: AbortSignal
}): Promise<TData[]> {
  const getPageArgs = (page: number) => ({
    page,
    pageSize,
    ...(signal ? { signal } : {}),
  })

  throwIfAborted(signal)

  const firstResponse = await fetchPage(getPageArgs(1))
  throwIfAborted(signal)

  const rows = [...getRowsFromIndexResponse(firstResponse)]
  const lastPage = Number(readProperty(readProperty(firstResponse, 'meta'), 'last_page') ?? 1)

  for (let page = 2; page <= lastPage; page += 1) {
    throwIfAborted(signal)

    const response = await fetchPage(getPageArgs(page))

    throwIfAborted(signal)

    rows.push(...getRowsFromIndexResponse(response))
  }

  return rows
}

export function fetchIndexTableQueryPage<TResult, TKey extends QueryKey>(
  queryOptions: ExportQueryOptions<TResult, TKey>,
  { signal }: { signal?: AbortSignal } = {}
): TResult | Promise<TResult> {
  if (typeof queryOptions?.queryFn !== 'function') {
    throw new Error('Index table export query is missing a query function.')
  }

  return queryOptions.queryFn({
    queryKey: queryOptions.queryKey,
    meta: queryOptions.meta,
    signal,
  })
}

export function useIndexTableSpreadsheetExport<TData, TSheets extends readonly unknown[]>({
  columns,
  filename,
  sheetName,
  rowCount,
  rows,
  loadRows,
  buildSheets,
  errorMessage = 'Failed to download spreadsheet',
}: {
  columns?: IndexTableExportColumnSource<TData>[]
  filename?: unknown
  sheetName?: string
  rowCount?: number
  rows?: TData[]
  loadRows?: (options: { signal: AbortSignal }) => Promise<TData[]>
  buildSheets?: (rows: TData[]) => IndexTableExportSheets<TSheets>
  errorMessage?: string
}): { exportDisabled: boolean; exportPending: boolean; handleExport: () => Promise<void> } {
  const abortControllerRef = useRef<AbortController | null>(null)
  const exportDisabled = rowCount === 0
  const exportMutation = useMutation(
    {
      mutationFn: async ({ signal }: { signal: AbortSignal }) => {
        const exportRows = loadRows ? await loadRows({ signal }) : rows
        throwIfAborted(signal)

        const sheets = buildSheets ? buildSheets(exportRows ?? []) : undefined
        throwIfAborted(signal)

        return createIndexDataTableSpreadsheetDownload({
          columns,
          rows: exportRows ?? [],
          sheets,
          filename,
          sheetName,
          signal,
        })
      },
    },
    queryClient
  )

  const handleExport = async () => {
    if (exportMutation.isPending || abortControllerRef.current || exportDisabled) {
      return
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const download = await exportMutation.mutateAsync({ signal: abortController.signal })
      throwIfAborted(abortController.signal)

      await waitForBrowserPaint()
      throwIfAborted(abortController.signal)

      downloadBlob(download.blob, download.filename)
    } catch (error) {
      if (abortController.signal.aborted || isAbortError(error)) {
        return
      }

      toast.error(errorMessage)
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
      }
    }
  }

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  return {
    exportDisabled,
    exportPending: exportMutation.isPending,
    handleExport,
  }
}
